import { BaseColor } from '@prophecy/ui';
import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BackEndCodeLanguage, DefaultTransformationEntityType } from '../../../graphqlTypes/enums';
import { useTeamProjectQuery } from '../../projectHooks/useTeamProjectQuery';
import { GitDiffResponse } from '../../queries/git';
import { useCommitDiff } from '../useCommitDiff';
import { useGitInfo } from '../useGitInfo';
import { useIsProjectAdmin } from '../useIsProjectAdmin';
import { useProjectBranchQuery } from '../useProjectBranchQuery';
import { useVersionQuery } from '../useVersionQuery';
import { getPreviousCommit } from '../utils';

const useProjectContext = ({
  projectId,
  enabled = true,
  isSimpleVersionControl = false,
  isExampleProject,
  defaultTransformationEntity,
  currentProjectStatus
}: {
  projectId: string;
  enabled?: boolean;
  isSimpleVersionControl?: boolean;
  isExampleProject: boolean;
  defaultTransformationEntity?: DefaultTransformationEntityType;
  currentProjectStatus?: string;
}) => {
  const currentProjectId = enabled ? projectId : '';

  const { isProjectAdmin, isInitialLoading: isAdminLoading } = useIsProjectAdmin(currentProjectId);
  const { data, isInitialLoading: isGitInfoLoading } = useGitInfo(projectId);
  const language = data?.Project?.language || BackEndCodeLanguage.python;
  const backendLanguage = data?.Project?.backendLanguage || BackEndCodeLanguage.python;
  const isSqlProject = data?.Project?.language === BackEndCodeLanguage.sql;
  const hasAnalysisProject = data?.Project?.hasAnalysis;

  const { name: projectName = '', forkMode, mainBranch, externalSubPath, industry, projectType } = data?.Project || {};

  const unCommittedCount = data?.Project?.workingGitContext?.uncommittedFilesCount || 0;
  const { branch: currentBranch, isInitialLoading } = useProjectBranchQuery(currentProjectId);
  const isMainBranch = mainBranch === currentBranch;

  return useMemo(
    () => ({
      backendLanguage,
      isGitInfoLoading,
      isAdminLoading,
      hasAnalysisProject,
      industry,
      isLoading: isInitialLoading,
      mainBranch: mainBranch as string,
      projectName,
      currentBranch,
      language,
      forkMode,
      unCommittedCount,
      projectId,
      hasProjectDataFetchEnabled: enabled,
      isProjectAdmin,
      isSqlProject,
      isMainBranch: isMainBranch,
      externalSubPath,
      isSimpleVersionControl,
      currentProjectStatus,
      isExampleProject,
      projectType,
      defaultTransformationEntity
    }),
    [
      backendLanguage,
      currentBranch,
      hasAnalysisProject,
      currentProjectStatus,
      defaultTransformationEntity,
      enabled,
      externalSubPath,
      forkMode,
      industry,
      isAdminLoading,
      isExampleProject,
      isGitInfoLoading,
      isInitialLoading,
      isMainBranch,
      isProjectAdmin,
      isSimpleVersionControl,
      isSqlProject,
      language,
      mainBranch,
      projectId,
      projectName,
      projectType,
      unCommittedCount
    ]
  );
};

const simpleStatus = {
  SUCCEEDED: 'Success',
  FAILED: 'Failed',
  RUNNING: 'Running',
  PENDING: 'Pending',
  CANCELLED: 'Cancelled',
  TIMEDOUT: 'Timedout',
  BLOCKED: 'Blocked',
  INTERNAL_ERROR: 'Internal Error',
  UNKNOWN: 'Unknown'
};

const statusColor = {
  SUCCEEDED: BaseColor.green,
  FAILED: BaseColor.error,
  RUNNING: BaseColor.yellow,
  PENDING: BaseColor.gray,
  CANCELLED: BaseColor.gray,
  TIMEDOUT: BaseColor.gray,
  BLOCKED: BaseColor.gray,
  INTERNAL_ERROR: BaseColor.gray,
  UNKNOWN: BaseColor.gray
};

export const simplifyVersionDeploymentStatus = (status: keyof typeof simpleStatus) => {
  if (!status) return { status: null, color: BaseColor.gray };

  return {
    status: simpleStatus[status],
    color: statusColor[status]
  };
};

const useVersionContext = ({
  projectId,
  isSimpleVersionControl,
  currentBranch
}: {
  projectId: string;
  isSimpleVersionControl: boolean;
  currentBranch: string;
}) => {
  const { data: versionData, isInitialLoading } = useVersionQuery(projectId);
  const [searchParams] = useSearchParams();

  const versionName = searchParams.get('versionName');
  const commitId = searchParams.get('version');
  const [globalDiff, setGlobalDiff] = useState<GitDiffResponse | null>(null);

  const data = versionData;

  const { getDiff } = useCommitDiff(projectId, currentBranch);

  useEffect(() => {
    if (versionName && commitId && data?.Versions) {
      const previousCommit = getPreviousCommit(data?.Versions, commitId);

      getDiff(commitId, previousCommit).then((diff) => {
        setGlobalDiff(diff);
      });
    } else {
      setGlobalDiff(null);
    }
  }, [versionName, commitId, data, getDiff, setGlobalDiff]);

  return { data, isInitialLoading, globalDiff };
};

const useTeamProjectContext = (teamUID: string) => {
  const { data, isInitialLoading } = useTeamProjectQuery(teamUID);
  return { data, isInitialLoading };
};

export type ProjectContextTypeReturnType = ReturnType<typeof useProjectContext>;

export const ProjectContext = React.createContext<ProjectContextTypeReturnType | undefined>(undefined);
const VersionContext = React.createContext<ReturnType<typeof useVersionContext> | null>(null);
const TeamProjectContext = React.createContext<ReturnType<typeof useTeamProjectContext> | null>(null);

export const ProjectCreationContext = React.createContext({
  selectProphecyManaged: false,
  setSelectProphecyManaged: (selectProphecyManaged: boolean) => {},
  gitAllowedTeams: [] as string[],
  setGitAllowedTeams: (gitAllowedTeams: string[]) => {}
});

export const ProjectCreationContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [selectProphecyManaged, setSelectProphecyManaged] = useState(false);
  const [gitAllowedTeams, setGitAllowedTeams] = useState<string[]>([]);

  return (
    <ProjectCreationContext.Provider
      value={{ selectProphecyManaged, setSelectProphecyManaged, gitAllowedTeams, setGitAllowedTeams }}>
      {children}
    </ProjectCreationContext.Provider>
  );
};

export function useTeamProjectState() {
  const context = useContext(TeamProjectContext) as ReturnType<typeof useTeamProjectContext>;
  if (context === undefined) {
    const error = 'useTeamProjectState must be used within a TeamProjectContextProvider';
    console.error(error);
    throw new Error(error);
  }
  return context;
}
export function useVersionState() {
  const context = useContext(VersionContext) as ReturnType<typeof useVersionContext>;
  if (context === undefined) {
    const error = 'useVersionState must be used within a VersionContextProvider';
    console.error(error);
    throw new Error(error);
  }
  return context;
}

/**
 * Returns the current version and whether the current version is in the version history view
 * @returns { currentVersion: string, isVersionHistoryView: boolean}
 */
export const useVersionInfo = () => {
  const [searchParams] = useSearchParams();
  const currentVersion = searchParams.get('version') || '';
  const versionName = searchParams.get('versionName') || '';
  const isVersionHistoryView = !!currentVersion;
  return { currentVersion, versionName, isVersionHistoryView };
};

export function TeamProjectContextProvider({ teamUID, children }: { teamUID: string; children: React.ReactNode }) {
  const teamProjectContextValue = useTeamProjectContext(teamUID);
  return <TeamProjectContext.Provider value={teamProjectContextValue}>{children}</TeamProjectContext.Provider>;
}

export function VersionContextProvider({
  projectId = '',
  children,
  isSimpleVersionControl = false,
  currentBranch = ''
}: {
  projectId: string;
  children: React.ReactNode;
  isSimpleVersionControl?: boolean;
  currentBranch?: string;
}) {
  const versionContextValue = useVersionContext({ projectId, isSimpleVersionControl, currentBranch });
  return <VersionContext.Provider value={versionContextValue}>{children}</VersionContext.Provider>;
}

export const ProjectContextProvider = memo(function ProjectContextProvider({
  children,
  projectId = '',
  enabled = true,
  isSimpleVersionControl = false,
  isExampleProject = false,
  defaultTransformationEntity,
  currentProjectStatus
}: {
  children: React.ReactNode;
  projectId?: string;
  enabled?: boolean;
  isSimpleVersionControl?: boolean;
  isExampleProject?: boolean;
  defaultTransformationEntity?: DefaultTransformationEntityType;
  currentProjectStatus?: string;
}) {
  const projectContextValue = useProjectContext({
    projectId,

    enabled,

    isSimpleVersionControl,

    isExampleProject,
    defaultTransformationEntity,
    currentProjectStatus
  });

  return <ProjectContext.Provider value={projectContextValue}>{children}</ProjectContext.Provider>;
});
